import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Feature } from "geojson";
import { enqueueSnackbar } from "notistack";

import { selectLayerByVersionAndLayerKey, selectVersionConfig } from "fond/api";
import { mergeLayerAttributes } from "fond/draw/attributeHelpers";
import FeatureAttributesForm from "fond/draw/FeatureAttributesForm";
import { HistoryContext, useHistory } from "fond/history";
import { MapContext } from "fond/map/MapProvider";
import { Configuration } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

/**
 * Resolves a layer ID to its parent if it's a sublayer
 * @returns The resolved layer ID (parent ID if sublayer, original ID otherwise)
 */
const resolveLayerId = (layerId: string | undefined, versionConfig: Configuration | undefined): string | undefined => {
  if (!layerId) return undefined;

  const config = versionConfig?.Data?.entities?.[layerId];
  if (config?.Type === "SUBLAYER" && config.ParentID) {
    const parentConfig = versionConfig?.Data?.entities?.[config.ParentID];
    if (parentConfig?.Type === "LAYER") {
      return parentConfig.Key;
    }
  }

  return layerId;
};

const PropertiesPanelEdit: React.FC = () => {
  const { map, drawControl } = useContext(MapContext);
  const { set: setHistory } = useHistory(HistoryContext);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const versionId = useAppSelector((state) => state.project.versionId);
  const versionConfig = useAppSelector((state) => selectVersionConfig(state, versionId));

  // Get editingLayerId from drawControl options
  const editingLayerId = drawControl.current?.options?.editOptions?.editingLayerId;

  // Get the layer configuration with attributes
  // Try direct lookup first, then fallback to parent if it's a sublayer
  const layerConfig = useAppSelector((state) => {
    if (!editingLayerId) return undefined;

    const directLayer = selectLayerByVersionAndLayerKey(state, { versionId, layerId: editingLayerId });
    if (directLayer) return directLayer;

    // Not found directly, try resolving as sublayer
    const resolvedId = resolveLayerId(editingLayerId, versionConfig);
    return resolvedId ? selectLayerByVersionAndLayerKey(state, { versionId, layerId: resolvedId }) : undefined;
  });

  // Get the current EditingLayer configuration from allLayers
  // Try direct lookup first, then fallback to parent if it's a sublayer
  const editingLayer = React.useMemo(() => {
    if (!editingLayerId) return undefined;

    const directLayer = drawControl.current?.options?.editOptions?.allLayers?.find((l) => l.ID === editingLayerId);
    if (directLayer) return directLayer;

    // Not found directly, try resolving as sublayer
    const resolvedId = resolveLayerId(editingLayerId, versionConfig);
    return drawControl.current?.options?.editOptions?.allLayers?.find((l) => l.ID === resolvedId);
  }, [editingLayerId, versionConfig, drawControl]);

  const handleOnSelect = useCallback(() => {
    if (drawControl.current?.getSelectedIds().length === 1) {
      const feature = drawControl.current?.getSelected().features[0];
      setSelectedFeature(feature || null);
    } else {
      setSelectedFeature(null);
    }
  }, [drawControl]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
    };
  }, [map, handleOnSelect]);

  const handleSubmit = useCallback(
    (_values: Record<string, string | number | boolean>) => {
      // Update history silently after attribute values are set
      if (drawControl.current?.getAll()) {
        setHistory(drawControl.current.getAll(), { silent: true });
      }

      // Refresh the selected feature after save
      if (selectedFeature?.id && drawControl.current) {
        const updatedFeature = drawControl.current.get(selectedFeature.id as string);
        if (updatedFeature) {
          setSelectedFeature(updatedFeature as Feature);
        }
      }
      enqueueSnackbar("Feature attributes updated", { variant: "success" });
    },
    [selectedFeature, drawControl, setHistory]
  );

  if (!selectedFeature) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <Typography variant="body2" color="textSecondary">
          Select a feature to edit properties
        </Typography>
      </Box>
    );
  }

  // Merge attributes from EditingLayer with layer schema
  const attributes = mergeLayerAttributes(layerConfig?.Attributes, editingLayer);

  if (attributes.length === 0) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={2}>
        <Typography variant="body2" color="textSecondary">
          This layer has no editable attributes
        </Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box p={2} pb={1}>
        <Typography variant="subtitle2" gutterBottom>
          {layerConfig?.Configuration.Label || editingLayerId}
        </Typography>
      </Box>
      <Box flex={1} sx={{ minHeight: 0 }}>
        <FeatureAttributesForm feature={selectedFeature} attributes={attributes} onSubmit={handleSubmit} showReset disableOnPristine />
      </Box>
    </Box>
  );
};

export default PropertiesPanelEdit;
